import React, { useEffect, useState } from 'react';
import { Modal, Avatar, Typography, Spin, Empty, Button } from 'antd';
import {
  RobotOutlined,
  ReloadOutlined,
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import useChatBotConversation from './ChatBotConversation/useChatBotConversation';
import ChatSessionList from './ChatBotConversation/ChatSessionList';
import ChatConversationPanel from './ChatBotConversation/ChatConversationPanel';
import { getInitial, formatDateTime } from './ChatBotConversation/utils';

const { Text } = Typography;

/**
 * 聊天類節點（chatBot / chatOrderingCart）對話檢視彈窗。
 * 支援 WhatsApp 風格媒體預覽、聯絡人名稱顯示，及全螢幕模式。
 */
const ChatBotConversationModal = ({
  visible,
  onClose,
  stepExecutionId,
  taskName,
  nodeKind = 'chatBot'
}) => {
  const { t } = useLanguage();
  const [fullscreen, setFullscreen] = useState(false);

  const isOrderingCart = nodeKind === 'chatOrderingCart';
  const titleKey = isOrderingCart
    ? 'workflowMonitor.chatOrderingCartConversationTitle'
    : 'workflowMonitor.chatBotConversationTitle';
  const titleFallback = isOrderingCart ? '聊天購物車對話' : '聊天機械人對話';
  const emptySessionsKey = isOrderingCart
    ? 'workflowMonitor.noChatOrderingCartSessions'
    : 'workflowMonitor.noChatBotSessions';
  const emptySessionsFallback = isOrderingCart
    ? '此節點暫無購物車對話紀錄'
    : '暫無對話紀錄';
  const loadFailedKey = isOrderingCart
    ? 'workflowMonitor.loadChatOrderingCartSessionsFailed'
    : 'workflowMonitor.loadChatBotSessionsFailed';
  const adminHintKey = isOrderingCart
    ? 'workflowMonitor.chatOrderingCartAdminInterventionHint'
    : 'workflowMonitor.chatBotAdminInterventionHint';
  const adminHintFallback = isOrderingCart
    ? '此處發送的訊息為 admin 介入，不會寫入聊天購物車對話歷史'
    : '此處發送的訊息為 admin 介入，不會寫入 ChatBot 對話歷史';

  const {
    loading,
    sessions,
    stepInfo,
    activeSessionId,
    setActiveSessionId,
    activeSession,
    combinedMessages,
    interventionMessages,
    inputText,
    setInputText,
    sending,
    loadSessions,
    handleSendIntervention,
    reset
  } = useChatBotConversation({
    mode: 'stepExecution',
    stepExecutionId,
    enabled: visible,
    loadFailedFallback: t(loadFailedKey) || (isOrderingCart ? '載入聊天購物車對話失敗' : '載入聊天機械人對話失敗')
  });

  useEffect(() => {
    if (visible) {
      setFullscreen(false);
      loadSessions();
    } else {
      reset();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const contactLabel = activeSession
    ? activeSession.contactName
      ? `${activeSession.contactName}  ${activeSession.waId}`
      : activeSession.waId
    : '';

  const headerTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, paddingRight: 8 }}>
      <Avatar
        size={36}
        src={activeSession?.aiStaffAvatarUrl || undefined}
        style={{ backgroundColor: '#1677ff', flexShrink: 0 }}
        icon={!activeSession?.aiStaffAvatarUrl ? <RobotOutlined /> : undefined}
      >
        {!activeSession?.aiStaffAvatarUrl ? getInitial(activeSession?.aiStaffName) : null}
      </Avatar>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <Text strong>
          {t(titleKey) || titleFallback}
          {(taskName || stepInfo?.taskName) && (
            <Text type="secondary" style={{ marginInlineStart: 8, fontSize: 13, fontWeight: 'normal' }}>
              · {taskName || stepInfo?.taskName}
            </Text>
          )}
        </Text>
        {activeSession && (
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {contactLabel}
            {activeSession.aiStaffName && <span style={{ margin: '0 6px' }}>·</span>}
            {activeSession.aiStaffName}
            <span style={{ margin: '0 6px' }}>·</span>
            {activeSession.updatedAt ? formatDateTime(activeSession.updatedAt) : ''}
          </Text>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 'auto' }}>
        <Button
          type="text"
          size="small"
          icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={(e) => { e.stopPropagation(); setFullscreen((v) => !v); }}
        />
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          onClick={onClose}
          style={{ color: '#ff4d4f' }}
        />
      </div>
    </div>
  );

  const modalWidth = fullscreen ? '100%' : 920;
  const modalStyles = fullscreen
    ? {
        wrapper: { overflow: 'hidden' },
        content: {
          height: '100vh',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0
        },
        header: { flexShrink: 0, marginBottom: 0 },
        body: {
          flex: 1,
          padding: 0,
          overflow: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }
    : {
        body: {
          padding: 0,
          height: 600,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={headerTitle}
      width={modalWidth}
      centered={!fullscreen}
      destroyOnClose
      footer={null}
      rootClassName={
        fullscreen ? 'chatbot-conversation-modal--fullscreen' : 'chatbot-conversation-modal'
      }
      styles={modalStyles}
      closeIcon={false}
    >
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      ) : sessions.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12
          }}
        >
          <Empty description={t(emptySessionsKey) || emptySessionsFallback} />
          <Button icon={<ReloadOutlined />} onClick={loadSessions}>
            {t('workflowMonitor.refresh') || '重新整理'}
          </Button>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          <ChatSessionList
            sessions={sessions}
            activeSessionId={activeSessionId}
            interventionMessages={interventionMessages}
            onSelect={setActiveSessionId}
            onRefresh={loadSessions}
            width={fullscreen ? 300 : 260}
          />
          <ChatConversationPanel
            messages={combinedMessages}
            activeSession={activeSession}
            inputText={inputText}
            onInputChange={setInputText}
            onSend={handleSendIntervention}
            sending={sending}
            visible={visible}
            adminHintKey={adminHintKey}
            adminHintFallback={adminHintFallback}
          />
        </div>
      )}
    </Modal>
  );
};

export default ChatBotConversationModal;
